import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useEffect } from "react";
import { isPhpApi } from "@/lib/backend-mode";
import { api } from "@/lib/api";

export type FinanceKind = "income" | "expense";

export type FinanceMatchStatus = "unmatched" | "matched" | "manual";
export type FinancePaymentMethod = "bank" | "cash" | "momo" | "other";

export interface FinanceEntry {
  id: string;
  kind: FinanceKind;
  amount: number;
  currency: string;
  category: string;
  occurred_on: string;
  note: string | null;
  item_name: string | null;
  quantity: number | null;
  unit_price: number | null;
  task_id: string | null;
  project_id: string | null;
  customer_id?: string | null;
  payer_name: string | null;
  payment_method: FinancePaymentMethod | null;
  match_status: FinanceMatchStatus;
  revenue_stream?: string;
  auto: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const CATEGORY_LABEL: Record<string, string> = {
  "lam-do": "Làm đồ",
  "thue-cosplay": "Cho thuê cosplay",
  "order": "Order đặt hộ",
  "vat-lieu": "Vật liệu",
  "ship": "Vận chuyển",
  "marketing": "Marketing",
  "khac": "Khác",
};

export const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }));

const VND = new Intl.NumberFormat("vi-VN");
export function formatVnd(n: number): string {
  return VND.format(Math.round(n || 0)) + "₫";
}
export function formatNumber(n: number): string {
  return VND.format(Math.round(n || 0));
}
/** Parse "1.500.000" / "1,500,000" / "1500000" / "1.5tr" / "500k" → number */
export function parseAmount(s: string): number {
  if (!s) return 0;
  let t = s.toString().trim().toLowerCase().replace(/[\s₫đ]/g, "");
  let mul = 1;
  const sm = t.match(/(k|tr|m|b)$/);
  if (sm) {
    mul = sm[1] === "k" ? 1_000 : sm[1] === "b" ? 1_000_000_000 : 1_000_000;
    t = t.slice(0, -sm[1].length);
  }
  // Một dấu . hoặc , kèm 1-2 chữ số phía sau ⇒ phần thập phân (vd: 1.5tr → 1.5)
  // Nhiều dấu hoặc >2 chữ số sau dấu ⇒ dấu phân tách hàng nghìn (vd: 1.500.000)
  const dec = t.match(/^(\d+)[.,](\d{1,2})$/);
  const n = dec ? Number(dec[1] + "." + dec[2]) : Number(t.replace(/[.,]/g, "")) || 0;
  return n * mul;
}

export function useFinanceEntries(params?: { from?: string; to?: string }) {
  const qc = useQueryClient();
  useEffect(() => {
    if (isPhpApi()) return; // realtime chỉ có ở Supabase mode
    const ch = supabase.channel("finance_entries_rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "finance_entries" }, () => {
        qc.invalidateQueries({ queryKey: ["finance_entries"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [qc]);

  return useQuery({
    queryKey: ["finance_entries", params?.from ?? null, params?.to ?? null],
    queryFn: async () => {
      if (isPhpApi()) {
        const q: Record<string, string> = {};
        if (params?.from) q.from = params.from;
        if (params?.to)   q.to   = params.to;
        return await api.get<FinanceEntry[]>("/finance", q);
      }
      let q = supabase.from("finance_entries").select("*")
        .order("occurred_on", { ascending: false })
        .order("created_at", { ascending: false });
      if (params?.from) q = q.gte("occurred_on", params.from);
      if (params?.to) q = q.lte("occurred_on", params.to);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as FinanceEntry[];
    },
  });
}

export function useUpsertFinance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (e: Partial<FinanceEntry> & { kind: FinanceKind; amount: number }) => {
      const payload: any = {
        kind: e.kind,
        amount: e.amount,
        currency: e.currency ?? "VND",
        category: e.category ?? "khac",
        occurred_on: e.occurred_on ?? new Date().toISOString().slice(0, 10),
        note: e.note ?? null,
        item_name: e.item_name ?? null,
        quantity: e.quantity ?? null,
        unit_price: e.unit_price ?? null,
        task_id: e.task_id ?? null,
        project_id: e.project_id ?? null,
        customer_id: (e as any).customer_id ?? null,
        payer_name: e.payer_name ?? null,
        payment_method: e.payment_method ?? null,
        match_status: e.match_status ?? "manual",
        auto: e.auto ?? false,
      };
      if (isPhpApi()) {
        return e.id
          ? await api.put<FinanceEntry>(`/finance/${encodeURIComponent(e.id)}`, payload)
          : await api.post<FinanceEntry>(`/finance`, payload);
      }
      if (e.id) {
        const { data, error } = await supabase.from("finance_entries").update(payload).eq("id", e.id).select().single();
        if (error) throw error;
        return data as FinanceEntry;
      }
      const { data: u } = await supabase.auth.getUser();
      payload.created_by = u.user?.id ?? null;
      const { data, error } = await supabase.from("finance_entries").insert(payload).select().single();
      if (error) throw error;
      return data as FinanceEntry;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance_entries"] }),
    onError: (e: any) => toast.error(e.message),
  });
}

export function useDeleteFinance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (isPhpApi()) { await api.del(`/finance/${encodeURIComponent(id)}`); return; }
      const { error } = await supabase.from("finance_entries").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance_entries"] }),
    onError: (e: any) => toast.error(e.message),
  });
}

export async function autoCreateFromTask(input: {
  task_id: string;
  project_id: string;
  price?: number;
  cost?: number;
  category: string;
  title: string;
}): Promise<{ income_id?: string; expense_id?: string }> {
  const out: { income_id?: string; expense_id?: string } = {};
  const today = new Date().toISOString().slice(0, 10);
  if (isPhpApi()) {
    if (input.price && input.price > 0) {
      const r = await api.post<{ id: string }>(`/finance`, {
        kind: "income", amount: input.price, currency: "VND",
        category: input.category, occurred_on: today,
        note: input.title, item_name: input.title,
        task_id: input.task_id, project_id: input.project_id, auto: true,
      });
      out.income_id = r.id;
    }
    if (input.cost && input.cost > 0) {
      const r = await api.post<{ id: string }>(`/finance`, {
        kind: "expense", amount: input.cost, currency: "VND",
        category: "vat-lieu", occurred_on: today,
        note: `Chi phí: ${input.title}`, item_name: input.title,
        task_id: input.task_id, project_id: input.project_id, auto: true,
      });
      out.expense_id = r.id;
    }
    return out;
  }
  if (input.price && input.price > 0) {
    const { data, error } = await supabase.from("finance_entries").insert({
      kind: "income", amount: input.price, currency: "VND",
      category: input.category, occurred_on: today,
      note: input.title, item_name: input.title,
      task_id: input.task_id, project_id: input.project_id, auto: true,
    }).select("id").single();
    if (error) throw error;
    out.income_id = data.id;
  }
  if (input.cost && input.cost > 0) {
    const { data, error } = await supabase.from("finance_entries").insert({
      kind: "expense", amount: input.cost, currency: "VND",
      category: "vat-lieu", occurred_on: today,
      note: `Chi phí: ${input.title}`, item_name: input.title,
      task_id: input.task_id, project_id: input.project_id, auto: true,
    }).select("id").single();
    if (error) throw error;
    out.expense_id = data.id;
  }
  return out;
}

export async function deleteAutoFromTask(taskId: string) {
  if (isPhpApi()) { await api.post(`/finance/delete-auto-by-task`, { task_id: taskId }); return; }
  await supabase.from("finance_entries").delete().eq("task_id", taskId).eq("auto", true);
}

/** Period presets matching the user's spreadsheet (Hôm nay / Tuần / Tháng / Quý / Năm) */
export type PeriodPreset = "today" | "week" | "month" | "quarter" | "year" | "7" | "30" | "90" | "ytd" | "all";
export function computeRange(p: PeriodPreset): { from: string; to: string } {
  const today = new Date();
  const to = today.toISOString().slice(0, 10);
  let from: Date;
  const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
  switch (p) {
    case "today": from = new Date(y, m, d); break;
    case "week": {
      const dow = (today.getDay() + 6) % 7; // Mon=0
      from = new Date(y, m, d - dow);
      break;
    }
    case "month": from = new Date(y, m, 1); break;
    case "quarter": from = new Date(y, Math.floor(m / 3) * 3, 1); break;
    case "year":
    case "ytd": from = new Date(y, 0, 1); break;
    case "all": from = new Date(2000, 0, 1); break;
    default: from = new Date(y, m, d - Number(p) + 1);
  }
  return { from: from.toISOString().slice(0, 10), to };
}

export function exportFinanceCsv(rows: FinanceEntry[]): string {
  // Lossless — snake_case header, raw values. Round-trip an toàn theo `id`.
  const header = [
    "id", "occurred_on", "kind", "category", "item_name", "quantity", "unit_price",
    "amount", "currency", "note", "auto", "revenue_stream",
    "customer_id", "task_id", "project_id",
    "payer_name", "payment_method", "match_status",
    "created_by", "created_at", "updated_at",
  ];
  const esc = (v: any) => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.map(esc).join(",")];
  for (const r of rows) {
    const row = header.map((k) => {
      const v = (r as unknown as Record<string, unknown>)[k];
      if (v == null) return "";
      if (typeof v === "boolean") return v ? "1" : "0";
      return String(v);
    });
    lines.push(row.map(esc).join(","));
  }
  return "\uFEFF" + lines.join("\r\n");
}
